import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Plus,
    Loader2,
    Copy,
    Check,
    Trash2,
    Building2,
    School as SchoolIcon,
    Users as UsersIcon,
    Mail,
} from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";

const ROLE_LABELS = {
    platform_admin: "Platform Admin",
    reviewer: "Reviewer",
    org_leader: "Org Leader",
    coach: "Coach",
    teacher: "Teacher",
};

function inviteUrlFor(inv) {
    if (inv.invite_url && inv.invite_url.startsWith("http")) return inv.invite_url;
    return `${window.location.origin}/accept-invitation/${inv.token}`;
}

export default function AdminOrgDetailPage() {
    const { orgId } = useParams();
    const [org, setOrg] = useState(null);
    const [schools, setSchools] = useState([]);
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [schoolOpen, setSchoolOpen] = useState(false);
    const [schoolName, setSchoolName] = useState("");
    const [schoolSubmitting, setSchoolSubmitting] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [orgRes, schoolsRes, usersRes, invRes] = await Promise.all([
                api.get(`/admin/organizations/${orgId}`),
                api.get(`/admin/organizations/${orgId}/schools`),
                api.get(`/admin/users`, { params: { org_id: orgId } }),
                api.get(`/admin/invitations`, { params: { org_id: orgId } }),
            ]);
            setOrg(orgRes.data);
            setSchools(schoolsRes.data);
            setUsers(usersRes.data);
            setInvitations(invRes.data);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load organization"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const handleCreateSchool = async (e) => {
        e.preventDefault();
        setSchoolSubmitting(true);
        try {
            await api.post(`/admin/organizations/${orgId}/schools`, {
                name: schoolName.trim(),
            });
            toast.success("School created");
            setSchoolName("");
            setSchoolOpen(false);
            load();
        } catch (err) {
            toast.error(extractError(err, "Could not create school"));
        } finally {
            setSchoolSubmitting(false);
        }
    };

    const revokeInvitation = async (id) => {
        try {
            await api.delete(`/admin/invitations/${id}`);
            toast.success("Invitation revoked");
            load();
        } catch (err) {
            toast.error(extractError(err, "Could not revoke invitation"));
        }
    };

    const copyInviteUrl = async (inv) => {
        try {
            await navigator.clipboard.writeText(inviteUrlFor(inv));
            setCopiedToken(inv.token);
            toast.success("Invitation URL copied");
            setTimeout(() => setCopiedToken(null), 2000);
        } catch (e) {
            toast.error("Copy failed. Select the URL manually.");
        }
    };

    if (loading) {
        return (
            <div className="text-[#0B2545] flex items-center gap-3" data-testid="org-loading">
                <Loader2 className="animate-spin" size={18} /> Loading organization…
            </div>
        );
    }
    if (error) {
        return <div className="text-red-700" data-testid="org-detail-error">{error}</div>;
    }

    return (
        <div className="space-y-8" data-testid="admin-org-detail-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Organization
                </div>
                <div className="flex items-center gap-3">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B2545] text-white">
                        <Building2 size={18} />
                    </div>
                    <div>
                        <h1
                            className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight"
                            data-testid="org-name"
                        >
                            {org.name}
                        </h1>
                        <div className="text-xs text-[#0E1B2C]/60">/{org.slug}</div>
                    </div>
                </div>
            </div>

            {/* Schools */}
            <section className="rounded-2xl bg-white border border-[#D6E6F2] p-6" data-testid="schools-section">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 text-[#0B2545]">
                        <SchoolIcon size={16} />
                        <h2 className="font-serif text-xl font-semibold">Schools</h2>
                        <span className="text-xs text-[#0E1B2C]/60">({schools.length})</span>
                    </div>
                    <Dialog open={schoolOpen} onOpenChange={setSchoolOpen}>
                        <DialogTrigger asChild>
                            <Button
                                size="sm"
                                className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                data-testid="create-school-btn"
                            >
                                <Plus size={14} /> New school
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="create-school-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-[#0B2545]">
                                    Create school
                                </DialogTitle>
                                <DialogDescription>Add a campus to {org.name}.</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleCreateSchool} className="space-y-4" data-testid="create-school-form">
                                <div className="space-y-2">
                                    <Label htmlFor="school-name">School name</Label>
                                    <Input
                                        id="school-name"
                                        required
                                        value={schoolName}
                                        onChange={(e) => setSchoolName(e.target.value)}
                                        placeholder="Riverside High School"
                                        data-testid="school-name-input"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button
                                        type="submit"
                                        disabled={schoolSubmitting}
                                        className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                        data-testid="create-school-submit"
                                    >
                                        {schoolSubmitting ? (
                                            <Loader2 className="animate-spin" size={16} />
                                        ) : (
                                            <Plus size={16} />
                                        )}
                                        Create
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {schools.length === 0 ? (
                    <div className="text-sm text-[#0E1B2C]/60">No schools yet.</div>
                ) : (
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="schools-list">
                        {schools.map((s) => (
                            <li
                                key={s.id}
                                className="rounded-lg border border-[#D6E6F2] px-4 py-3 flex items-center justify-between bg-[#F8FBFF]"
                                data-testid={`school-row-${s.id}`}
                            >
                                <div>
                                    <div className="font-semibold text-[#0B2545]">{s.name}</div>
                                    <div className="text-xs text-[#0E1B2C]/60">
                                        Created {new Date(s.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Users */}
            <section className="rounded-2xl bg-white border border-[#D6E6F2] p-6" data-testid="users-section">
                <div className="flex items-center gap-2 text-[#0B2545] mb-4">
                    <UsersIcon size={16} />
                    <h2 className="font-serif text-xl font-semibold">Users</h2>
                    <span className="text-xs text-[#0E1B2C]/60">({users.length})</span>
                    <Link
                        to="/app/admin/invitations/new"
                        className="ml-auto text-sm text-[#1E56A0] hover:underline"
                        data-testid="invite-user-link"
                    >
                        Invite user →
                    </Link>
                </div>
                {users.length === 0 ? (
                    <div className="text-sm text-[#0E1B2C]/60">No users yet.</div>
                ) : (
                    <div className="overflow-x-auto" data-testid="users-list">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#0E1B2C]/60 border-b border-[#D6E6F2]">
                                    <th className="py-2 pr-4">Name</th>
                                    <th className="py-2 pr-4">Email</th>
                                    <th className="py-2 pr-4">Role</th>
                                    <th className="py-2 pr-4">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className="border-b border-[#D6E6F2]/70" data-testid={`user-row-${u.id}`}>
                                        <td className="py-2 pr-4 font-medium text-[#0B2545]">
                                            {u.full_name || "—"}
                                        </td>
                                        <td className="py-2 pr-4">{u.email}</td>
                                        <td className="py-2 pr-4">{ROLE_LABELS[u.role] || u.role}</td>
                                        <td className="py-2 pr-4">
                                            {u.is_active ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs">
                                                    <Check size={12} /> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-xs">
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Pending Invitations */}
            <section className="rounded-2xl bg-white border border-[#D6E6F2] p-6" data-testid="invitations-section">
                <div className="flex items-center gap-2 text-[#0B2545] mb-4">
                    <Mail size={16} />
                    <h2 className="font-serif text-xl font-semibold">Pending Invitations</h2>
                    <span className="text-xs text-[#0E1B2C]/60">({invitations.length})</span>
                </div>
                {invitations.length === 0 ? (
                    <div className="text-sm text-[#0E1B2C]/60">No pending invitations.</div>
                ) : (
                    <ul className="space-y-3" data-testid="invitations-list">
                        {invitations.map((inv) => (
                            <li
                                key={inv.id}
                                className="rounded-lg border border-[#D6E6F2] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between bg-[#F8FBFF]"
                                data-testid={`invitation-row-${inv.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-semibold text-[#0B2545] truncate">{inv.email}</div>
                                    <div className="text-xs text-[#0E1B2C]/60">
                                        {ROLE_LABELS[inv.role] || inv.role} · expires{" "}
                                        {new Date(inv.expires_at).toLocaleDateString()}
                                    </div>
                                    <div
                                        className="mt-2 text-xs text-[#1E56A0] break-all font-mono"
                                        data-testid={`invitation-url-${inv.id}`}
                                    >
                                        {inviteUrlFor(inv)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => copyInviteUrl(inv)}
                                        className="rounded-full border-[#1E56A0] text-[#1E56A0] hover:bg-[#D6E6F2]"
                                        data-testid={`copy-invite-${inv.id}`}
                                    >
                                        {copiedToken === inv.token ? <Check size={14} /> : <Copy size={14} />}
                                        {copiedToken === inv.token ? "Copied" : "Copy link"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => revokeInvitation(inv.id)}
                                        className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                                        data-testid={`revoke-invite-${inv.id}`}
                                    >
                                        <Trash2 size={14} />
                                        Revoke
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
